import { useCallback, useEffect, useRef, useState } from 'react';
import type { NetworkGraph, Occupancy, StateSnapshot } from '@mrt/graph';
import { fetchGraph, fetchState } from './api.js';

/** F4: the client fetches every 20s; the server polls upstream every 10s. */
const FETCH_INTERVAL_MS = 20_000;
const BACKOFF_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;
/** Past this without a usable snapshot, the client says so rather than pretending. */
const CLIENT_STALE_MS = 90_000;

export type ClientStatus = 'connecting' | 'live' | 'stale' | 'down' | 'offline';

export interface LiveState {
  graph: NetworkGraph | null;
  occupancies: Occupancy[];
  status: ClientStatus;
  /** Epoch ms, on the CLIENT clock, of the last snapshot we successfully read. */
  lastSnapshotAt: number | null;
  /** serverNow - clientNow, so server timestamps can be read against the local clock. */
  clockOffsetMs: number;
  graphError: string | null;
}

/**
 * Owns all network activity. While the document is hidden it does nothing at
 * all — no fetches, and the caller stops animating — and on resume it refetches
 * immediately rather than waiting out the interval.
 */
export function useLiveState(): LiveState {
  const [graph, setGraph] = useState<NetworkGraph | null>(null);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<StateSnapshot | null>(null);
  const [lastSnapshotAt, setLastSnapshotAt] = useState<number | null>(null);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [reachable, setReachable] = useState(true);
  const [, forceTick] = useState(0);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graphTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readout = useRef<ReturnType<typeof setInterval> | null>(null);
  const failures = useRef(0);
  const stopped = useRef(false);
  /** Bumped per request so a slow response cannot overwrite a newer one. */
  const generation = useRef(0);

  const clearTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const tick = useCallback(async () => {
    if (stopped.current || document.hidden) return;
    const gen = ++generation.current;
    try {
      const next = await fetchState();
      if (stopped.current) return;
      // A response that lost the race is discarded: applying it would rewind
      // the map, and stamping it fresh would make stale data look live.
      if (gen !== generation.current) return;
      const arrivedAt = Date.now();
      setSnapshot(next);
      setLastSnapshotAt(arrivedAt);
      // Phone clocks drift; occupancy timestamps are on the server's clock.
      setClockOffsetMs(next.now - arrivedAt);
      setReachable(true);
      failures.current = 0;
    } catch {
      if (stopped.current || gen !== generation.current) return;
      setReachable(false);
      failures.current = Math.min(failures.current + 1, BACKOFF_MS.length);
    } finally {
      if (!stopped.current && !document.hidden && gen === generation.current) {
        const delay = failures.current === 0 ? FETCH_INTERVAL_MS : BACKOFF_MS[failures.current - 1]!;
        clearTimer();
        timer.current = setTimeout(() => void tick(), delay);
      }
    }
  }, []);

  useEffect(() => {
    stopped.current = false;
    let cancelled = false;

    const loadGraph = async () => {
      if (cancelled || document.hidden) return;
      try {
        const g = await fetchGraph();
        if (!cancelled) {
          setGraph(g);
          setGraphError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setGraphError(err instanceof Error ? err.message : String(err));
        // The graph is static; retrying is always worthwhile and never urgent —
        // but it must not keep firing while the app is backgrounded.
        graphTimer.current = setTimeout(() => void loadGraph(), 5_000);
      }
    };

    // Re-render once a second so the freshness readout counts up even when no
    // new snapshot arrives. This is state display, not animation, and there is
    // nothing to display while hidden.
    const startReadout = () => {
      if (readout.current === null) readout.current = setInterval(() => forceTick((n) => n + 1), 1_000);
    };
    const stopReadout = () => {
      if (readout.current !== null) clearInterval(readout.current);
      readout.current = null;
    };

    void loadGraph();
    void tick();
    startReadout();

    const onVisibility = () => {
      if (document.hidden) {
        // Zero network and no wake-ups while backgrounded.
        clearTimer();
        if (graphTimer.current !== null) clearTimeout(graphTimer.current);
        graphTimer.current = null;
        stopReadout();
      } else {
        // Reconcile immediately on resume rather than waiting out the interval.
        failures.current = 0;
        void tick();
        if (!graph) void loadGraph();
        startReadout();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stopped.current = true;
      clearTimer();
      if (graphTimer.current !== null) clearTimeout(graphTimer.current);
      graphTimer.current = null;
      stopReadout();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // `graph` is read only inside the resume handler to decide whether a retry
    // is still needed; re-running this effect on every snapshot would restart
    // the poll loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const status: ClientStatus = (() => {
    if (!reachable) return 'offline';
    if (!snapshot) return 'connecting';
    if (lastSnapshotAt !== null && Date.now() - lastSnapshotAt > CLIENT_STALE_MS) return 'stale';
    return snapshot.feedStatus;
  })();

  return {
    graph,
    occupancies: snapshot?.occupancies ?? [],
    status,
    lastSnapshotAt,
    clockOffsetMs,
    graphError,
  };
}
